import { ReactElement, useCallback, useMemo } from 'react'
import { isElement } from 'react-is'
import IconClear from 'assets/clear.svg?react'
import cn from 'classnames'
import { IconTooltipError } from 'components/IconTooltip'
import { isRequiredError } from 'helpers/error'
import { ButtonIcon } from 'shared/components/ButtonIcon'
import { IInputBaseProps, IInputPropsGenericExtend, InputBase } from 'shared/ui/InputBase'

import s from './Input.module.less'
import cs from 'src/common.module.less'

export interface IInputProps<InputProps extends {}> extends IInputBaseProps<InputProps> {
  icon?: TSvgIconComponent | ReactElement
  isClearable?: boolean
  isShowTooltip?: boolean
}

export const Input = <InputProps extends IInputPropsGenericExtend>({
  icon: Icon,
  isClearable,
  value,
  error,
  warning,
  prefix,
  postfix,
  inputRef,
  inputProps = {} as InputProps,
  onChange,
  isShowTooltip,
  ...props
}: IInputProps<InputProps>) => {
  const hasError = error && !isRequiredError(error)

  const handleClear = useCallback(() => {
    if (onChange) {
      onChange('')
    }
  }, [onChange])

  const renderPrefix = useCallback(() => {
    if (Icon) {
      return isElement(Icon) ? (
        // eslint-disable-next-line react/jsx-pascal-case
        <Icon.type {...Icon.props} className={cn(Icon.props.className, cs.marginR4)} />
      ) : (
        // @ts-ignore
        <Icon className={cs.marginR4} />
      )
    }

    if (prefix) {
      return <div className={cn(cs.flex, cs.marginR4)}>{prefix}</div>
    }

    return null
  }, [Icon, prefix])

  const renderPostfix = useCallback(() => {
    // @ts-expect-error
    const currentValue = value === undefined ? inputRef?.current?.value || '' : value
    const hasClearButton = currentValue && isClearable

    if (hasClearButton || hasError || warning || postfix) {
      return (
        <div className={cn(cs.flex, cs.flexAlignItemsCenter, cs.marginL4)}>
          {postfix}

          {hasClearButton ? <ButtonIcon color="dark" icon={IconClear} onClick={handleClear} /> : null}

          {isShowTooltip && (hasError || warning) ? (
            <IconTooltipError
              containerSize="nm"
              placement="top-end"
              className={cn(s.errorIcon, {
                [s.errorIcon_padded]: hasClearButton,
                [s.errorIcon_warning]: !hasError && warning
              })}
            >
              {error || warning}
            </IconTooltipError>
          ) : null}
        </div>
      )
    }

    return null
  }, [error, handleClear, hasError, inputRef, isClearable, isShowTooltip, postfix, value, warning])

  const customInputProps = useMemo(
    () => ({
      ...inputProps,
      className: cn(s.input, inputProps?.className)
    }),
    [inputProps]
  )

  return (
    <InputBase
      {...props}
      ref={inputRef}
      error={error}
      inputProps={customInputProps}
      onChange={onChange}
      postfix={renderPostfix()}
      prefix={renderPrefix()}
      value={value}
      warning={warning}
    />
  )
}
